// Public mobile-app download page (no auth) served at /app.
// The printed QR codes point here rather than straight at the APK, so the
// download URL can change without reprinting anything, and so iPhone users
// land on an explanation instead of an .apk their phone cannot open.
//
// Version and download URLs come from GET /api/health/mobile-version — the
// same source of truth the app's own update gate reads, so this page can
// never advertise a version that the backend does not know about.
import React, { useEffect, useState } from 'react';

import { APP_QR_SVG } from '@/features/auth/pages/appQrCode';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';

interface MobileVersion {
  minVersion: string;
  latestVersion: string;
  androidUrl: string | null;
  iosUrl: string | null;
  message: string | null;
}

type Platform = 'android' | 'ios' | 'other';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  // iPadOS 13+ reports itself as a Mac, so check for touch support too.
  if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }
  return 'other';
}

const STEPS: string[] = [
  'Appuyez sur « Télécharger pour Android ». Le fichier .apk arrive dans vos téléchargements.',
  'Ouvrez le fichier téléchargé. Android affiche un avertissement : c’est normal pour une application installée hors du Play Store.',
  'Appuyez sur « Paramètres », activez « Autoriser depuis cette source », puis revenez en arrière.',
  'Appuyez sur « Installer », puis sur « Ouvrir ». Connectez-vous avec vos identifiants NEXEDU habituels.',
];

export const AppDownloadPage: React.FC = () => {
  const [info, setInfo] = useState<MobileVersion | null>(null);
  const [failed, setFailed] = useState(false);
  const platform = detectPlatform();

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/health/mobile-version`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<MobileVersion>;
      })
      .then((data) => {
        if (!cancelled) setInfo(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const androidUrl = info?.androidUrl ?? null;

  return (
    <div className="min-h-screen bg-[#FBFBF9] text-[#1F2A24]">
      <header className="border-b border-[#E4E7E4] bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
          <span className="text-xl font-extrabold tracking-widest text-[#0F3A2C]">NEXEDU</span>
          <a href="/login" className="text-sm text-[#5C6560] hover:text-[#0F3A2C]">
            Se connecter
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="text-3xl font-bold text-[#0F3A2C]">Application mobile NEXEDU</h1>
        <p className="mt-3 max-w-2xl text-[#5C6560]">
          Consultez vos notes, votre emploi du temps, vos absences et vos paiements depuis votre
          téléphone. Réservée aux étudiants, parents et enseignants.
        </p>

        <div className="mt-8 grid gap-6 md:grid-cols-[1fr_auto]">
          {/* Android */}
          <section className="rounded-2xl border border-[#E4E7E4] bg-white p-6">
            <h2 className="text-lg font-semibold text-[#0F3A2C]">Android</h2>

            {androidUrl ? (
              <a
                href={androidUrl}
                className="mt-4 inline-block rounded-xl bg-[#0F3A2C] px-6 py-3 font-semibold text-white hover:bg-[#15503C]"
              >
                Télécharger pour Android
              </a>
            ) : (
              <p className="mt-4 rounded-xl bg-[#F4F2ED] px-4 py-3 text-sm text-[#5C6560]">
                {failed
                  ? 'Lien de téléchargement momentanément indisponible. Réessayez dans quelques minutes.'
                  : 'Chargement du lien de téléchargement…'}
              </p>
            )}

            {info && (
              <p className="mt-3 text-sm text-[#5C6560]">
                Version {info.latestVersion} · Android 7.0 ou plus récent
              </p>
            )}

            <ol className="mt-6 space-y-3 text-sm text-[#3F4A44]">
              {STEPS.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0F3A2C] text-xs font-semibold text-white">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* QR — useless on the phone that is already here, so hide it there */}
          {platform === 'other' && (
            <aside className="flex flex-col items-center rounded-2xl border border-[#E4E7E4] bg-white p-6">
              <div
                className="h-40 w-40"
                // eslint-disable-next-line react/no-danger -- static build-time SVG, no user input
                dangerouslySetInnerHTML={{ __html: APP_QR_SVG }}
              />
              <p className="mt-3 max-w-[10rem] text-center text-xs text-[#5C6560]">
                Scannez avec votre téléphone pour ouvrir cette page
              </p>
            </aside>
          )}
        </div>

        {/* iOS */}
        <section className="mt-6 rounded-2xl border border-[#E4E7E4] bg-white p-6">
          <h2 className="text-lg font-semibold text-[#0F3A2C]">iPhone et iPad</h2>
          {info?.iosUrl ? (
            <a
              href={info.iosUrl}
              className="mt-4 inline-block rounded-xl bg-[#0F3A2C] px-6 py-3 font-semibold text-white hover:bg-[#15503C]"
            >
              Télécharger pour iPhone
            </a>
          ) : (
            <p className="mt-3 text-sm text-[#5C6560]">
              La version iPhone arrive prochainement. En attendant, NEXEDU reste accessible depuis
              le navigateur de votre iPhone, avec les mêmes identifiants.
            </p>
          )}
          {platform === 'ios' && !info?.iosUrl && (
            <a
              href="/login"
              className="mt-4 inline-block rounded-xl bg-[#0F3A2C] px-6 py-3 font-semibold text-white hover:bg-[#15503C]"
            >
              Ouvrir NEXEDU dans le navigateur
            </a>
          )}
        </section>

        <p className="mt-8 text-xs text-[#8A918C]">
          Besoin d&apos;aide ? Contactez l&apos;administration de votre établissement.{' '}
          <a href="/legal/privacy" className="underline hover:text-[#0F3A2C]">
            Politique de confidentialité
          </a>
        </p>
      </main>
    </div>
  );
};
